import { useEffect, useState } from "react";
import { Button, Card, Cluster, FormField, Input, Stack, useToast } from "@salesapp/ui";
import { reportSaveFailure, reportSaved } from "../../lib/saveNotice";
import { crmApi, type LossReasonOption } from "../../lib/crmApi";
import { FIELD_HINTS } from "../../help/fieldHints";

function slugify(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "reason";
}

export function LossReasonSettingsPanel({ orgId }: { orgId: string }) {
  const { show } = useToast();
  const [reasons, setReasons] = useState<LossReasonOption[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void crmApi
      .getLossReasons(orgId)
      .then((res) => setReasons(res.reasons))
      .catch((err) => setError(err instanceof Error ? err.message : "Loss reasons could not be loaded."));
  }, [orgId]);

  function patchReason(index: number, next: Partial<LossReasonOption>) {
    setReasons((current) => {
      if (!current) return current;
      const copy = [...current];
      copy[index] = { ...copy[index], ...next };
      return copy;
    });
    setSaved(false);
  }

  function removeReason(index: number) {
    setReasons((current) => (current ? current.filter((_, i) => i !== index) : current));
    setSaved(false);
  }

  function addReason() {
    setReasons((current) => [...(current ?? []), { code: "", label: "", description: "" }]);
    setSaved(false);
  }

  async function save() {
    if (!reasons) return;
    setSaving(true);
    setError(null);
    try {
      const cleaned = reasons.map((reason) => ({
        code: reason.code.trim() || slugify(reason.label),
        label: reason.label.trim(),
        description: reason.description?.trim() || "",
      }));
      const result = await crmApi.putLossReasons(orgId, cleaned);
      setReasons(result.reasons);
      setSaved(true);
      reportSaved(show);
    } catch (err) {
      setError(reportSaveFailure(show, err, "Loss reasons could not be saved."));
    } finally {
      setSaving(false);
    }
  }

  if (!reasons) {
    return <p className="home-subtitle">{error ?? "Loading loss reasons…"}</p>;
  }

  return (
    <Stack gap={4}>
      {error ? <p className="crm-error">{error}</p> : null}
      <p className="home-subtitle">
        These reasons appear as checkboxes when a deal is closed as lost, so trend reporting on why deals are lost
        stays consistent across the team.
      </p>
      <Stack gap={3}>
        {reasons.map((reason, index) => (
          <Card key={index}>
            <Stack gap={3}>
              <FormField label="Label" tooltip={FIELD_HINTS.loss_reason_label}>
                <Input
                  value={reason.label}
                  onChange={(event) => patchReason(index, { label: event.target.value })}
                  placeholder="e.g. Competitor advantage"
                />
              </FormField>
              <FormField
                label="Tooltip description"
                hint="Shown on hover as a trimmed reminder of what this reason means."
                tooltip={FIELD_HINTS.loss_reason_tooltip}
              >
                <Input
                  value={reason.description ?? ""}
                  onChange={(event) => patchReason(index, { description: event.target.value })}
                  placeholder="Short explanation for the tooltip"
                />
              </FormField>
              <Cluster gap={2}>
                <Button type="button" variant="secondary" onClick={() => removeReason(index)}>
                  Remove
                </Button>
              </Cluster>
            </Stack>
          </Card>
        ))}
      </Stack>
      <Cluster gap={2}>
        <Button type="button" variant="secondary" onClick={addReason}>
          Add reason
        </Button>
        <Button type="button" onClick={() => save()} disabled={saving}>
          {saving ? "Saving…" : "Save loss reasons"}
        </Button>
        {saved ? <span className="crm-muted">Saved</span> : null}
      </Cluster>
    </Stack>
  );
}
